import React from "react";
import {
  CircularProgress,
  Paper,
  Tooltip,
  Typography,
  Divider,
} from "@mui/material";
import { addDays } from "date-fns";
import { List, Lock } from "react-feather";
import styled from "styled-components";
import LibraryShowModule from "../pages/private/library/components/LibraryShowModule";
import useQuizReview from "../swr/hooks/Library/useQuizReview";
import useTeamMemberBreakdownInfo from "../swr/hooks/RetailReports/singleTeamMemberV2/useTeamMemberBreakdownInfo";
import useGlobalStore from "src/store/useGlobalStore";

const Wrapper = styled(Paper)`
  padding: 20px 20px 20px 20px;
  border-radius: 6px;
  min-width: 280px;
  width: 100%;
`;

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: fit-content;
  padding: 4px;
  border-radius: 4px;
  margin-right: 4px;

  &:hover {
    cursor: pointer;
    background-color: #eeeeee;
  }
`;

const InnerContainer = styled.div`
  width: 100%;
  background-color: white;
  border-radius: 4px;
  min-height: 100px;
  display: flex;
  padding: 4px;
  align-items: flex-start;
  flex-direction: column;
  justify-content: flex-start;

  min-height: 156px;

  span {
    color: black;
  }
`;

const Centered = styled.div`
  padding-top: 10px;
  display: flex;
  min-height: 20px;
  align-items: center;
  justify-content: flex-start;
`;

const OutstandingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 300px;
  height: auto;
  overflow-y: scroll;
`;

const FlexDiv = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 2px;
`;

const StyledSpan = styled.span`
  color: black;
  font-size: 11px;
  font-weight: 400;
`;

const Inner = ({ id }) => {
  const { details: data, isLoading } = useQuizReview(id);
  const config = useGlobalStore((state) => state.config);

  if (isLoading) {
    return (
      <InnerContainer>
        <div
          style={{
            width: "100%",
            minHeight: 140,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span style={{ color: "darkgrey" }}>loading...</span>
        </div>
      </InnerContainer>
    );
  }
  return (
    <InnerContainer>
      <LibraryShowModule
        srcString={data.image}
        value={data.name}
        marketplaceParentInfo={
          Object.values(data.marketPlaceParent).length > 0
            ? { companyName: data.marketPlaceParent.company.name }
            : null
        }
        config={config}
      />
      <FlexDiv>
        <StyledSpan>Start date:</StyledSpan>
        <StyledSpan>
          {addDays(new Date(data.startDate), 1).toDateString()}
        </StyledSpan>
      </FlexDiv>
      <FlexDiv>
        <StyledSpan>End date:</StyledSpan>
        <StyledSpan>
          {data.dueDate
            ? addDays(new Date(data.dueDate), 1).toDateString()
            : "---/---"}
        </StyledSpan>
      </FlexDiv>
      <FlexDiv>
        <StyledSpan>Time to complete:</StyledSpan>
        <StyledSpan>{data?.estimatedTime} minutes</StyledSpan>
      </FlexDiv>
      <FlexDiv>
        <StyledSpan>Score to pass:</StyledSpan>
        <StyledSpan>
          {data?.passScore}/{data?.questions?.questions?.length}{" "}
        </StyledSpan>
      </FlexDiv>
      <FlexDiv>
        <StyledSpan>Has Description:</StyledSpan>
        <StyledSpan>{data.description ? "Yes" : "No"}</StyledSpan>
      </FlexDiv>
      <FlexDiv>
        <StyledSpan>Has Disclaimer:</StyledSpan>
        <StyledSpan>{data?.disclaimer ? "Yes" : "No"}</StyledSpan>
      </FlexDiv>
      <FlexDiv>
        <StyledSpan>Answer Key Enabled:</StyledSpan>
        <StyledSpan>{data?.displayAnswers ? "Yes" : "No"}</StyledSpan>
      </FlexDiv>
    </InnerContainer>
  );
};

const OutstandingModules = ({ id, name }) => {
  const { data, isError, isLoading } = useTeamMemberBreakdownInfo(id);

  if (isLoading) {
    return (
      <Wrapper>
        <Centered>
          <CircularProgress size={30} />
        </Centered>
      </Wrapper>
    );
  }

  if (
    isError &&
    isError?.response?.data?.error?.title === "Permission Denied"
  ) {
    return (
      <Wrapper>
        <Typography
          sx={{ mt: -1, mb: -1 }}
          style={{ fontSize: 15, fontWeight: "500" }}
        >
          Outstanding Modules
        </Typography>
        <Centered
          style={{
            background: "#eeeeee",
            borderRadius: 4,
            height: 50,
            marginTop: 8,
            paddingTop: 0,
            paddingLeft: 6,
          }}
        >
          <Lock height={12} widht={12} color="firebrick" />
          <span
            style={{ fontSize: 12, color: "slategrey", fontStyle: "italic" }}
          >
            Premium feature
          </span>
        </Centered>
      </Wrapper>
    );
  }

  if (isError || !data) {
    return (
      <Wrapper>
        <Centered>
          <Typography variant="subtitle2">
            <em>Error Loading Outstanding Modules..</em>
          </Typography>
        </Centered>
      </Wrapper>
    );
  }

  const outstandingModules = data?.progress?.breakdown.filter(
    (quiz) => !quiz.completed,
  );

  if (outstandingModules.length === 0) {
    return (
      <Wrapper>
        <Centered>
          <Typography variant="subtitle2">
            {name} has 0 outstanding modules
          </Typography>
        </Centered>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <Typography
          sx={{ mt: -1, mb: -1 }}
          style={{ fontSize: 15, fontWeight: "500" }}
        >
          Outstanding Modules
        </Typography>
        <div
          style={{
            padding: "0px 3px",
            background: "#eeeeee",
            borderRadius: 6,
          }}
        >
          <span style={{ color: "black", fontSize: 11, fontWeight: 600 }}>
            {outstandingModules?.length}
          </span>
        </div>
      </div>
      <Divider sx={{ mb: 3, mt: 3 }} />
      <OutstandingWrapper>
        {outstandingModules.map((quiz) => (
          <div
            key={quiz.id}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              marginBottom: 4,
            }}
          >
            <span style={{ fontSize: 12, fontWeight: "400" }}>{quiz.name}</span>
            <Tooltip
              componentsProps={{
                tooltip: {
                  sx: {
                    backgroundColor: "white",
                    border: "1px solid slategrey",
                    maxHeight: "none",
                    padding: "0px",
                    minWidth: 300,
                    maxWidth: 400,
                  },
                },
              }}
              title={<Inner id={quiz.id} />}
              arrow
              placement="right"
            >
              <Container>
                <List height={12} width={12} color="darkgrey" />
              </Container>
            </Tooltip>
          </div>
        ))}
      </OutstandingWrapper>
    </Wrapper>
  );
};

export default OutstandingModules;
